import React from 'react';
import { MessageCircle, Instagram, MapPin, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import Layout from '../layout/Layout';
import PageHeader from '../layout/PageHeader';
import { businessInfo } from '../../data/mockData';
import { getDefaultInquiryURL } from '../../utils/whatsappGenerator';

const ContactPage = () => {
  return (
    <Layout>
      <PageHeader
        title="Contact Us"
        description="We'd love to hear from you. Reach out via WhatsApp for the fastest response."
        videoSrc="/images/contact_anime.mp4"
      />

      {/* Contact Methods */}
      <section className="relative z-[2] isolate overflow-visible bg-[#F7F2EE] py-16 md:py-20">
        <div className="pointer-events-none absolute -left-28 top-24 h-72 w-72 rounded-full bg-rose-200/30 blur-3xl" />
        <div className="pointer-events-none absolute -right-28 bottom-24 h-72 w-72 rounded-full bg-[#F5BCB1]/25 blur-3xl" />
        <div className="pointer-events-none absolute bottom-[-110px] left-[-230px] z-[1] hidden md:block" aria-hidden="true">
          <img
            src="/images/contact_flow.png"
            alt=""
            className="h-[760px] w-auto max-w-none -scale-x-100 object-contain object-bottom-left lg:h-[820px]"
          />
        </div>
        <div className="relative z-[20] max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {/* WhatsApp - Primary */}
            <Card className="rounded-[1.65rem] border border-rose-200/80 bg-gradient-to-br from-white/78 to-rose-50/74 shadow-[0_20px_65px_rgba(120,72,54,0.12)] backdrop-blur-xl md:col-span-2">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-2xl border border-rose-200/80 bg-white/80 flex items-center justify-center mx-auto mb-4 shadow-[0_10px_28px_rgba(251,113,133,0.14)]">
                  <MessageCircle className="w-8 h-8 text-[#FB7185]" />
                </div>
                <h2 className="font-serif text-2xl font-semibold text-[#3A2A2A] mb-2">
                  WhatsApp (Preferred)
                </h2>
                <p className="text-stone-600 mb-4">
                  The fastest way to reach us for orders and inquiries
                </p>
                <p className="text-lg font-medium text-[#3A2A2A] mb-6">
                  {businessInfo.whatsappNumber}
                </p>
                <a href={getDefaultInquiryURL()} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-[#F5BCB1] hover:bg-[#efaaa0] text-stone-900 px-8 border-0">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Message on WhatsApp
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* Instagram */}
            <Card className="rounded-[1.5rem] border border-rose-200/70 bg-white/68 shadow-[0_18px_55px_rgba(120,72,54,0.10)] backdrop-blur-xl">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-xl border border-rose-200/80 bg-gradient-to-br from-white/90 to-rose-100/70 flex items-center justify-center mx-auto mb-4 shadow-[0_10px_28px_rgba(251,113,133,0.14)]">
                  <Instagram className="w-6 h-6 text-[#FB7185]" />
                </div>
                <h3 className="font-serif text-lg font-semibold text-[#3A2A2A] mb-2">
                  Instagram
                </h3>
                <p className="text-stone-600 text-sm mb-4">
                  Follow us for inspiration and updates
                </p>
                <a 
                  href={businessInfo.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#FB7185] hover:text-rose-600 font-medium transition-colors"
                >
                  @flowberry_los
                </a>
              </CardContent>
            </Card>

            {/* Location */}
            <Card className="rounded-[1.5rem] border border-rose-200/70 bg-white/68 shadow-[0_18px_55px_rgba(120,72,54,0.10)] backdrop-blur-xl">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-xl border border-rose-200/80 bg-gradient-to-br from-white/90 to-rose-100/70 flex items-center justify-center mx-auto mb-4 shadow-[0_10px_28px_rgba(251,113,133,0.14)]">
                  <MapPin className="w-6 h-6 text-[#FB7185]" />
                </div>
                <h3 className="font-serif text-lg font-semibold text-[#3A2A2A] mb-2">
                  Location
                </h3>
                <p className="text-stone-600 text-sm mb-2">
                  Based in Glendale, California
                </p>
                <p className="text-stone-600 text-sm">
                  Serving Los Angeles & LA County
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Working Hours */}
          <Card className="rounded-[1.5rem] border border-rose-200/70 bg-white/68 shadow-[0_18px_55px_rgba(120,72,54,0.10)] backdrop-blur-xl">
            <CardContent className="p-8">
              <div className="flex items-center justify-center gap-3 mb-6">
                <Clock className="w-6 h-6 text-[#FB7185]" />
                <h2 className="font-serif text-xl font-semibold text-[#3A2A2A]">
                  Working Hours
                </h2>
              </div>
              <div className="text-center">
                <p className="text-xl text-[#3A2A2A] font-medium mb-2">
                  {businessInfo.workingHours}
                </p>
                <p className="text-stone-600">
                  Fast WhatsApp response • Available for inquiries and orders throughout the week
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Service Note */}
          <div className="mt-12 text-center">
            <p className="text-stone-600 text-sm">
              For the fastest response, please contact us via WhatsApp. 
              We typically respond within a few hours during working hours.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
